/**
 * identity — device-keyed anonymous client identity for the web PWA.
 *
 * Phase 0 has no auth, but the experience still needs continuity: a returning
 * visitor should see their chat history and a name the
 * concierge can use. We mint a UUID on first visit, store it in localStorage,
 * and key all per-visitor state off it. Phase 2 swaps this for the JWT/passkey
 * `client_id` (and on first sign-in we migrate the localStorage thread into
 * DynamoDB so nothing is lost).
 *
 * Everything here is best-effort: localStorage may be unavailable (private
 * mode, embedded webviews). Callers should treat reads as possibly empty.
 */
const DEVICE_ID_KEY = "rp.deviceId";
const CHAT_KEY = "rp.chat.v1";
const NAME_KEY = "rp.name";

function safeRead(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage unavailable — silently degrade.
  }
}

function uuid(): string {
  // `crypto.randomUUID` is available on every browser we support; the fallback
  // is just defensive in case we get rendered in an odd embedded surface.
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `dev-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function getDeviceId(): string {
  let id = safeRead(DEVICE_ID_KEY);
  if (!id) {
    id = uuid();
    safeWrite(DEVICE_ID_KEY, id);
  }
  return id;
}

export interface StoredChatMessage {
  id: number;
  role: "bot" | "user";
  text: string;
}

export function loadChat(): StoredChatMessage[] | null {
  const raw = safeRead(CHAT_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      // A turn carrying a `listing` was written by the old ?listing= deep link
      // and its text names an invented property (spec 024): drop the whole
      // turn, not just the card.
      return (parsed as Record<string, unknown>[]).filter(
        (m) => !("listing" in m),
      ) as unknown as StoredChatMessage[];
    }
  } catch {
    // fall through
  }
  return null;
}

export function saveChat(messages: readonly StoredChatMessage[]): void {
  safeWrite(CHAT_KEY, JSON.stringify(messages));
}

export function clearChat(): void {
  try {
    localStorage.removeItem(CHAT_KEY);
  } catch {
    // ignore
  }
}

export function getClientName(): string | null {
  return safeRead(NAME_KEY);
}

export function setClientName(name: string): void {
  safeWrite(NAME_KEY, name);
}
